import L from 'leaflet';
import { faMinusSquare, faPlusSquare, faTimesSquare, faFileExcel } from '@fortawesome/pro-light-svg-icons';

import './search-results-control.scss';
import { ResultGroup } from './result-group';
import { SearchResultGroupModel, searchResultType } from '../../models/search-result-model';
import { getObjectClassName, getIcon, addEventHandler, getMapEvents, disableMapEvents, restoreMapEvents } from '../../helpers/control-helper';

L.Control.SearchResultsControl = L.Control.extend({
  initialize: function (options) { this.options = options; },
  onAdd: function () { return this.options.container; }
});

const DEFAULT_MENU_CONTROL_OPTIONS = {
  position: 'topright',
  visible: false,
  collapsed: false
};
const HIDDEN_CLASS_NAME = 'hidden';
const VISIBLE_CLASS_NAME = 'visible';
const COLLAPSED_CLASS_NAME = 'collapsed';
const EXPANDED_CLASS_NAME = 'expanded';
const NO_RESULTS_CLASS_NAME = 'no-results';
const HAS_RESULTS_CLASS_NAME = 'has-results';

class SearchResultsControl {
  constructor(leafletMap, excelExport = null, options = {}) {

    this._objectClassName = getObjectClassName(this);

    if (!(leafletMap instanceof L.Map)) throw new Error('Invalid "leafletMap" param supplied to "' + this._objectClassName + '.ctor"');
    if (typeof options !== 'object') throw new Error('Invalid "options" param supplied to "' + this._objectClassName + '.ctor"');

    options = { ...DEFAULT_MENU_CONTROL_OPTIONS, ...options };
    if (typeof options.position !== 'string') throw new Error('Invalid "options.position" param supplied to "' + this._objectClassName + '.ctor"');
    if (typeof options.visible !== 'boolean') throw new Error('Invalid "options.visible" param supplied to "' + this._objectClassName + '.ctor"');
    if (typeof options.collapsed !== 'boolean') throw new Error('Invalid "options.collapsed" param supplied to "' + this._objectClassName + '.ctor"');
    
    this._excelExport = excelExport;

    this._mapEvents = null;

    // Container
    this._container = L.DomUtil.create('div', 'leaflet-bar search-results-control ' +
      (options.visible ? VISIBLE_CLASS_NAME : HIDDEN_CLASS_NAME) + ' ' + (options.collapsed ? COLLAPSED_CLASS_NAME : EXPANDED_CLASS_NAME));
    L.DomEvent.on(this._container, 'mouseenter', this._disableMapEvents, this);
    L.DomEvent.on(this._container, 'mouseleave', this._enableMapEvents, this);
    // Header
    const header = L.DomUtil.create('div', 'header', this._container);
    L.DomUtil.create('span', '', header).innerHTML = 'Search results';
    const closeButton = L.DomUtil.create('span', 'btn-close', header);
    closeButton.innerHTML = getIcon(faTimesSquare, { title: 'Close' });
    addEventHandler(closeButton, 'click', this.close);
    this._collapseButton = L.DomUtil.create('span', 'btn-collapse', header);
    addEventHandler(this._collapseButton, 'click', () => this.collapsed = !this.collapsed);
    this._updateCollapseButton();

    //excel export button
    if (typeof this._excelExport === 'function' && this._excelExport !== undefined && this._excelExport !== null) {
      const excelExportButton = L.DomUtil.create('span', 'btn-excel-export', header);
      excelExportButton.innerHTML = getIcon(faFileExcel, { title: 'Export to Excel' });
      addEventHandler(excelExportButton, 'click', this.excelExport);
    }

    // Body
    this._body = L.DomUtil.create('div', 'body ' + NO_RESULTS_CLASS_NAME, this._container);
    const noResultsDiv = L.DomUtil.create('div', 'no-results-div', this._body);
    noResultsDiv.innerHTML = 'No results found';
    this._resultsDiv = L.DomUtil.create('div', 'results-div', this._body);

    this._onClose = null;
    this._onResultLabelClick = null;
    this._onResultIconClick = null;
    this._leafletMap = leafletMap;
    this._canScrollWheelZoom = this._leafletMap.scrollWheelZoom._enabled === true;

    this._leafletMap.addControl(new L.Control.SearchResultsControl({ position: options.position, container: this._container }))
  }

  update(searchResults) {
    if (!Array.isArray(searchResults)) {
      throw new Error('Invalid "searchResults" supplied to "SearchResultsControl.update"');
    }

    let searchGroups = SearchResultGroupModel.FromArray(searchResults);

    if (searchResults.length === 0) {
      L.DomUtil.addClass(this._body, NO_RESULTS_CLASS_NAME);
      L.DomUtil.removeClass(this._body, HAS_RESULTS_CLASS_NAME);
      this._body.style.maxHeight = undefined;
    } else {
      L.DomUtil.addClass(this._body, HAS_RESULTS_CLASS_NAME);
      L.DomUtil.removeClass(this._body, NO_RESULTS_CLASS_NAME);
      this._body.style.maxHeight = (this._leafletMap._size.y * 0.75) + 'px';
      this._resultsDiv.innerHTML = '';
      ResultGroup.FromSearchResultGroupModel(searchGroups, this._resultsDiv, this._intOnResultLabelClick, this._intOnResultIconClick);
    }

    this._searchResults = searchResults;
    this.collapsed = false;
    this.visible = true;
  }

  get visible() {
    const result = L.DomUtil.hasClass(this._container, VISIBLE_CLASS_NAME);
    return result;
  }
  set visible(value) {
    if (typeof value !== 'boolean') {
      throw new Error('Invalid "value" param supplied to "' + this._objectClassName + '.visible.set"');
    }

    if (this.visible !== value) {
      if (value) {
        L.DomUtil.addClass(this._container, VISIBLE_CLASS_NAME);
        L.DomUtil.removeClass(this._container, HIDDEN_CLASS_NAME);
      } else {
        L.DomUtil.addClass(this._container, HIDDEN_CLASS_NAME);
        L.DomUtil.removeClass(this._container, VISIBLE_CLASS_NAME);
      }
    }
  }

  get collapsed() {
    const result = L.DomUtil.hasClass(this._container, COLLAPSED_CLASS_NAME);
    return result;
  }
  set collapsed(value) {
    if (typeof value !== 'boolean') {
      throw new Error('Invalid "value" param supplied to "' + this._objectClassName + '.collapsed.set"');
    }

    if (this.collapsed !== value) {
      if (value) {
        L.DomUtil.addClass(this._container, COLLAPSED_CLASS_NAME);
        L.DomUtil.removeClass(this._container, EXPANDED_CLASS_NAME);
      } else {
        L.DomUtil.addClass(this._container, EXPANDED_CLASS_NAME);
        L.DomUtil.removeClass(this._container, COLLAPSED_CLASS_NAME);
      }
      this._updateCollapseButton();
    }
  }

  get onClose() { return this._onClose; }
  set onClose(value) {
    if (this._onClose !== value) {
      this._onClose = value;
    }
  }

  get onResultLabelClick() { return this._onResultLabelClick; }
  set onResultLabelClick(value) {
    if (this._onResultLabelClick !== value) {
      this._onResultLabelClick = value;
    }
  }

  get onResultIconClick() { return this._onResultIconClick; }
  set onResultIconClick(value) {
    if (this._onResultIconClick !== value) {
      this._onResultIconClick = value;
    }
  }

  _updateCollapseButton() {
    if (this.collapsed) {
      this._collapseButton.innerHTML = getIcon(faPlusSquare, { title: 'Expand' });
    } else {
      this._collapseButton.innerHTML = getIcon(faMinusSquare, { title: 'Collapse' });
    }
  }

  _disableMapEvents = () => {
    if (this._mapEvents === null) {
      this._mapEvents = getMapEvents(this._leafletMap);
      disableMapEvents(this._leafletMap);
    }
  }

  _enableMapEvents = () => {
    if (this._mapEvents !== null) {
      restoreMapEvents(this._leafletMap, this._mapEvents);
      this._mapEvents = null;
    }
  }

  close = () => {
    if (this.visible) {
      this.visible = false;
      if (typeof this._onClose === 'function') {
        this._onClose();
      }
    }
  }

  excelExport = () => {
    if (this._searchResults !== null && this._searchResults !== undefined && this._searchResults.length > 0) {
      let searchResults = this._searchResults.filter(x => x.results !== null && x.results !== undefined && x.results.length > 0).flatMap(x => x.results);
      this._excelExport(searchResults);
    }
  }

  _intOnResultLabelClick = (result) => {
    if (typeof this._onResultLabelClick === 'function' && this._onResultLabelClick(result) === true) {
      return;
    }
    this._intOnResultIconClick(result);
  }

  _intOnResultIconClick = (result) => {
    if (typeof this._onResultIconClick === 'function' && this._onResultIconClick(result) === true) {
      return;
    }

    const bounds = L.latLngBounds(result.shapeModel.latLngs);
    const zoom = Math.min(searchResultType.defaultZoomLevel(result.type),
      this._leafletMap.getBoundsZoom(bounds, 0));
    this._leafletMap.flyTo(bounds.getCenter(), zoom, { animate: false });
  }
}

export { SearchResultsControl };
